const STORAGE_KEY = 'rcsim-panel-collapsed';

type CollapsedMap = Record<string, boolean>;

function load(): CollapsedMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as CollapsedMap;
  } catch {
    return {};
  }
}

function save(map: CollapsedMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
}

/** True when the user last left this panel collapsed (default: expanded). */
export function isPanelCollapsed(id: string): boolean {
  return !!load()[id];
}

export function setPanelCollapsed(id: string, collapsed: boolean): void {
  const map = load();
  if (collapsed) map[id] = true;
  else delete map[id];
  save(map);
}

/** Opening markup for a collapsible `.panel` (`title` is plain text). */
export function panelStart(id: string, title: string): string {
  const open = isPanelCollapsed(id) ? '' : ' open';
  return `<details class="panel" data-panel="${escapeAttr(id)}"${open}><summary class="panel-summary"><span class="panel-title">${escapeHtml(title)}</span></summary>`;
}

export function panelEnd(): string {
  return `</details>`;
}

/** Wire persistence for every `details.panel[data-panel]` under `root`. Call after each render. */
export function bindPanelCollapse(root: HTMLElement): void {
  root.querySelectorAll<HTMLDetailsElement>('details.panel[data-panel]').forEach((el) => {
    el.addEventListener('toggle', () => {
      const id = el.dataset.panel;
      if (!id) return;
      setPanelCollapsed(id, !el.open);
    });
  });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
